/**
 * Bundled sample PROVIDER ROSTER (ProviderRosterRow) + PATIENT RECORDS
 * (PatientResponse) — demo fallback.
 *
 * Used by ProviderExperience (roster + patient detail), PatientSearch and
 * PatientRegistration when the deployed static site cannot reach the API:
 * the affected sections render these contract-exact samples with a
 * "Sample data" badge (prov.sampleData) instead of an empty/error panel, so
 * the whole demo stays fully clickable on pages.dev.
 *
 * Shapes mirror apps/api exactly:
 *   - ProviderRosterRow  (src/api/provider.ts) — GET /api/v1/provider/roster
 *   - PatientResponse    (src/lib/api.ts)      — GET /api/v1/patients/{id}
 *
 * The first row reuses the shared chart patient id
 * 7f381bff-5fa9-4fee-92a0-f8e21a163e19 so the roster, the detail identity
 * form, and the sample vitals/encounters/documents all describe the same
 * person (same NHI as the seeded demo row: SYR-1978-100001).
 *
 * ids are stable UUIDs — detail lookups match them verbatim.
 */
import type { ProviderRosterRow } from '../../api/provider'
import type { PatientResponse } from '../../lib/api'

/** The sample patient the detail sections (vitals/encounters/docs) key on. */
export const SAMPLE_CHART_PATIENT_ID = '7f381bff-5fa9-4fee-92a0-f8e21a163e19'

export const SAMPLE_ROSTER: ProviderRosterRow[] = [
  {
    patient_id: SAMPLE_CHART_PATIENT_ID,
    person_id: '7a111111-1111-4111-8111-111111111111',
    nhi: 'SYR-1978-100001',
    mrn: 'MRN-100001',
    name_ar: 'أحمد الخطيب',
    name_latin: 'Ahmad Al-Khatib',
    first_name: 'Ahmad',
    last_name: 'Al-Khatib',
    dob: '1978-04-12',
    sex: 'M',
    phone: '+963 944 100 001',
    address: { city: 'Damascus', governorate: 'Damascus' },
    facility_id: 'AL-NOUR-DMC',
    verification_tier: 3,
    status: 'active',
    registration_source: 'registration-desk',
    updated_at: '2026-08-27T09:15:00Z',
    medications_count: 2,
    family_history_record_count: 1,
  },
  {
    patient_id: '1b222222-2222-4222-8222-222222222222',
    person_id: '7a222222-2222-4222-8222-222222222222',
    nhi: 'SYR-1992-100002',
    mrn: 'MRN-100002',
    name_ar: 'مريم العباس',
    name_latin: 'Mariam Al-Abbas',
    first_name: 'Mariam',
    last_name: 'Al-Abbas',
    dob: '1992-09-03',
    sex: 'F',
    phone: '+963 944 100 002',
    address: { city: 'Homs', governorate: 'Homs' },
    facility_id: 'AL-NOUR-DMC',
    verification_tier: 2,
    status: 'active',
    registration_source: 'web-registration',
    updated_at: '2026-08-24T14:40:00Z',
    medications_count: 1,
    family_history_record_count: 0,
  },
  {
    patient_id: '1c333333-3333-4333-8333-333333333333',
    person_id: '7a333333-3333-4333-8333-333333333333',
    nhi: 'SYR-1985-100003',
    mrn: 'MRN-100003',
    name_ar: 'خالد المحمد',
    name_latin: 'Khaled Al-Mohammad',
    first_name: 'Khaled',
    last_name: 'Al-Mohammad',
    dob: '1985-01-27',
    sex: 'M',
    phone: '+963 944 100 003',
    address: { city: 'Aleppo', governorate: 'Aleppo' },
    facility_id: 'AL-NOUR-DMC',
    verification_tier: 1,
    status: 'active',
    registration_source: 'field-clinic',
    updated_at: '2026-08-20T08:05:00Z',
    medications_count: 0,
    family_history_record_count: 0,
  },
  {
    patient_id: '1d444444-4444-4444-8444-444444444444',
    person_id: '7a444444-4444-4444-8444-444444444444',
    nhi: 'SYR-1999-100004',
    mrn: 'MRN-100004',
    name_ar: 'نور الهدى',
    name_latin: 'Nour Al-Huda',
    first_name: 'Nour',
    last_name: 'Al-Huda',
    dob: '1999-06-15',
    sex: 'F',
    phone: '+963 944 100 004',
    address: { city: 'Latakia', governorate: 'Latakia' },
    facility_id: 'AL-NOUR-DMC',
    verification_tier: 4,
    status: 'active',
    registration_source: 'registration-desk',
    updated_at: '2026-08-18T11:30:00Z',
    medications_count: 0,
    family_history_record_count: 2,
  },
  {
    patient_id: '1e555555-5555-4555-8555-555555555555',
    person_id: '7a555555-5555-4555-8555-555555555555',
    nhi: 'SYR-1965-100005',
    mrn: 'MRN-100005',
    name_ar: '',
    name_latin: 'Fadia Al-Sayed',
    first_name: 'Fadia',
    last_name: 'Al-Sayed',
    dob: '1965-11-02',
    sex: 'F',
    phone: '+963 933 200 005',
    address: { city: 'Deir ez-Zor', governorate: 'Deir ez-Zor' },
    facility_id: 'AL-NOUR-DMC',
    verification_tier: 1,
    status: 'active',
    registration_source: 'field-clinic',
    updated_at: null,
    medications_count: 0,
    family_history_record_count: 0,
  },
  {
    patient_id: '1f666666-6666-4666-8666-666666666666',
    person_id: '7a666666-6666-4666-8666-666666666666',
    nhi: 'SYR-2010-100006',
    mrn: 'MRN-100006',
    name_ar: 'عمر الحسين',
    name_latin: 'Omar Al-Hussein',
    first_name: 'Omar',
    last_name: 'Al-Hussein',
    dob: '2010-03-21',
    sex: 'M',
    phone: '',
    address: { city: 'Daraa', governorate: 'Daraa' },
    facility_id: 'AL-NOUR-DMC',
    verification_tier: 2,
    status: 'active',
    registration_source: 'web-registration',
    updated_at: '2026-08-29T16:45:00Z',
    medications_count: 0,
    family_history_record_count: 1,
  },
]

/** Roster rows → PatientResponse for the detail page (same identity data). */
export function rosterRowToPatientResponse(row: ProviderRosterRow): PatientResponse {
  return {
    id: row.patient_id,
    person_id: row.person_id,
    facility_id: row.facility_id,
    mrn: row.mrn,
    verification_tier: row.verification_tier,
    status: row.status,
    person: {
      id: row.person_id,
      nhi: row.nhi,
      name_ar: row.name_ar,
      name_latin: row.name_latin,
      dob: row.dob,
      sex: row.sex === 'M' || row.sex === 'F' ? row.sex : null,
      phone: row.phone,
    },
  }
}

/** PatientResponse → roster-shaped row (used by offline search/registration). */
export function patientResponseToRosterRow(patient: PatientResponse): ProviderRosterRow {
  const latin = patient.person.name_latin ?? ''
  const parts = latin.trim().split(/\s+/)
  return {
    patient_id: patient.id,
    person_id: patient.person_id,
    nhi: patient.person.nhi,
    mrn: patient.mrn,
    name_ar: patient.person.name_ar ?? '',
    name_latin: latin,
    first_name: parts.length > 0 ? parts[0] : '',
    last_name: parts.length > 1 ? parts[parts.length - 1] : '',
    dob: patient.person.dob ?? '',
    sex: patient.person.sex === 'M' || patient.person.sex === 'F' ? patient.person.sex : '—',
    phone: patient.person.phone ?? '',
    address: null,
    facility_id: patient.facility_id,
    verification_tier:
      patient.verification_tier >= 1 && patient.verification_tier <= 4
        ? (patient.verification_tier as 1 | 2 | 3 | 4)
        : 1,
    status: patient.status,
    registration_source: null,
    updated_at: null,
    medications_count: 0,
    family_history_record_count: 0,
  }
}